import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Cake, MagnifyingGlass } from "@phosphor-icons/react";
import api from "../lib/api";
import PlayerAvatar from "../components/PlayerAvatar";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const selectCls = "h-11 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md text-sm";

const fmtDob = (dob) => {
  const [y, m, d] = dob.slice(0, 10).split("-");
  return `${Number(d)} ${MONTHS[Number(m) - 1].slice(0, 3)} ${y}`;
};

const dueLabel = (n) => (n === 0 ? "Today!" : n === 1 ? "Tomorrow" : `in ${n} days`);

const BirthdayRow = ({ p, highlight }) => (
  <Link
    to={`/players/${p.id}`}
    data-testid={`birthday-row-${p.id}`}
    className={`grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_2fr_1.3fr_1fr_auto] items-center gap-3 sm:gap-4 px-4 py-3 border-b border-neutral-900 last:border-0 hover:bg-[#181818] transition-colors ${highlight ? "bg-[#FFD166]/5" : ""}`}
  >
    <PlayerAvatar player={p} size={40} />
    <div className="min-w-0">
      <div className="font-semibold truncate">{p.first_name} {p.last_name}</div>
      <div className="text-xs text-neutral-500 sm:hidden">{fmtDob(p.date_of_birth)} · Turns {p.turning}</div>
      {p.team && <div className="text-xs text-neutral-500 hidden sm:block truncate">{p.team}</div>}
    </div>
    <div className="hidden sm:block text-sm text-neutral-300" data-testid={`birthday-dob-${p.id}`}>{fmtDob(p.date_of_birth)}</div>
    <div className="hidden sm:block text-sm text-neutral-400">Age {p.age} → <span className="text-white font-bold">{p.turning}</span></div>
    <div className="flex flex-col items-end gap-1">
      <span data-testid={`birthday-age-group-${p.id}`} className="px-2 py-0.5 rounded bg-[#D90429]/15 text-[#ff4d6a] text-xs font-black">{p.age_group || "—"}</span>
      <span className={`text-[11px] font-bold uppercase tracking-wider ${p.days_until <= 6 ? "text-[#FFD166]" : "text-neutral-500"}`}>{dueLabel(p.days_until)}</span>
    </div>
  </Link>
);

const Section = ({ title, items, testid, highlight }) => (
  <div className="bg-[#121212] border border-neutral-800 rounded-lg overflow-hidden" data-testid={testid}>
    <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-800">
      <div className={`font-display font-bold uppercase tracking-tight ${highlight ? "text-[#FFD166]" : ""}`}>{title}</div>
      <div className="overline">{items.length} player{items.length === 1 ? "" : "s"}</div>
    </div>
    {items.map((p) => <BirthdayRow key={p.id} p={p} highlight={highlight} />)}
  </div>
);

export default function Birthdays() {
  const [list, setList] = useState(null);
  const [settings, setSettings] = useState({});
  const [q, setQ] = useState("");
  const [team, setTeam] = useState("");
  const [ageGroup, setAgeGroup] = useState("");

  useEffect(() => {
    api.get("/players/birthdays").then((r) => setList(r.data));
    api.get("/settings").then((r) => setSettings(r.data || {}));
  }, []);

  const filtered = useMemo(() => (list || []).filter((p) =>
    (!team || p.team === team) && (!ageGroup || p.age_group === ageGroup) &&
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(q.toLowerCase())
  ), [list, q, team, ageGroup]);

  const thisWeek = filtered.filter((p) => p.days_until <= 6);
  const nowMonth = new Date().getMonth();
  const months = Array.from({ length: 12 }, (_, i) => (nowMonth + i) % 12)
    .map((m) => ({ m, items: filtered.filter((p) => p.birth_month === m + 1) }))
    .filter((g) => g.items.length);

  if (!list) return <div className="overline">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="birthdays-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline flex items-center gap-2"><Cake size={14} weight="bold" /> Squad</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Birthdays</h1>
          <div className="text-xs text-neutral-500 mt-2">{list.length} players with a date of birth · age groups set automatically</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input data-testid="birthdays-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search player" className={`${selectCls} pl-9 w-44`} />
          </div>
          <select data-testid="birthdays-filter-team" value={team} onChange={(e) => setTeam(e.target.value)} className={selectCls}>
            <option value="">All Teams</option>
            {(settings.teams || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select data-testid="birthdays-filter-age-group" value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} className={selectCls}>
            <option value="">All Age Groups</option>
            {(settings.age_groups || []).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {thisWeek.length > 0 && <Section title="This week" items={thisWeek} testid="birthdays-this-week" highlight />}

      {months.length ? months.map((g) => (
        <Section key={g.m} title={MONTHS[g.m]} items={g.items} testid={`birthdays-month-${g.m + 1}`} />
      )) : (
        <div className="text-neutral-500 text-sm" data-testid="birthdays-empty">No players match.</div>
      )}
    </div>
  );
}
